import { apiGet, apiPost, apiPut } from '../../../adapters/api-client/api';
import type { DefinitionStamp } from './integrity';

/**
 * PI-3 Epic 4 — admin/CTO/CEO dashboards API client.
 *
 * Read endpoints require ADMIN, MODERATOR, or SYSTEM. Config mutation and
 * the parent-progress audit trigger additionally require ADMIN.
 */

// ── Shared ───────────────────────────────────────────────────────────────────

export type RollingWindow = '24h' | '7d' | '30d';

export interface MetricValue {
  /** Null when the denominator is 0: N/A, never 0 %. */
  value: number | null;
  status: 'MEASURED' | 'N_A';
  numerator: number;
  denominator: number;
  rollingWindow: RollingWindow;
}

/**
 * ADM-13-FR-02..04 — an A03 class-health ratio as the product-health dashboard
 * reports it: the same definition, over a rolling window, with its stamp.
 * A zero denominator is N_A with a null value, never 0 %.
 */
export interface DefinedRatioMetric {
  numerator: number;
  denominator: number;
  value: number | null;
  status: 'MEASURED' | 'N_A';
  rollingWindow: RollingWindow;
  definition: DefinitionStamp;
}

// ── S4.1 ─────────────────────────────────────────────────────────────────────

export interface ProductHealthDashboard {
  generatedAt: string;
  cycleCompletionRate: DefinedRatioMetric;
  attendanceRate: DefinedRatioMetric;
  rubricCompletionRate: DefinedRatioMetric & { notApplicable: number };
  disputeRate: MetricValue;
  holdbackDistribution: {
    pending: number;
    released: number;
    refunded: number;
    rollingWindow: RollingWindow;
  };
  releaseAccuracy: {
    runId: string;
    completedAt: string;
    expectedTotal: number;
    observedTotal: number;
    mismatches: number;
    accuracy: number;
  } | null;
}

// ── S4.3 ─────────────────────────────────────────────────────────────────────

export interface TopTeacherRow {
  teacherId: string;
  releasedRolling30d: number;
  pendingInEscrow: number;
  holdbackInWindow: number;
  refundedTotal: number;
  enrollmentCount: number;
}

export interface EarningsDashboard {
  generatedAt: string;
  /** What every figure below is denominated in. */
  currency: string;
  totals: {
    releasedRolling7d: number;
    releasedRolling30d: number;
    pendingInEscrow: number;
    holdbackInWindow: number;
    refundedTotal: number;
    refundedByReason: { cancellation: number; dispute: number; noShow: number };
  };
  topTeachers: TopTeacherRow[];
}

// ── S4.4 ─────────────────────────────────────────────────────────────────────

export type Severity = 'info' | 'warning' | 'critical';

export interface StuckEscrowRow {
  enrollmentId: string;
  milestoneId: string;
  sequence: number;
  amount: number;
  completedAt: string;
  hoursStuck: number;
  severity: Severity;
}

export interface UnresolvedDisputeRow {
  disputeId: string;
  enrollmentId: string;
  milestoneId: string;
  status: string;
  openedAt: string;
  hoursOpen: number;
  severity: Severity;
}

export interface NotificationFailureRow {
  notificationId: string;
  enrollmentId: string;
  kind: string;
  attempts: number;
  lastError?: string;
  lastAttemptAt?: string;
}

export interface BookingExceptionRow {
  bookingId: string;
  enrollmentId: string;
  milestoneSequence: number;
  state: string;
  scheduledStart: string;
  hoursPastStart: number;
  severity: Severity;
}

export interface OpsDashboard {
  generatedAt: string;
  stuckEscrow: StuckEscrowRow[];
  unresolvedDisputes: UnresolvedDisputeRow[];
  notificationFailures: NotificationFailureRow[];
  bookingExceptions: BookingExceptionRow[];
}

// ── S4.5 ─────────────────────────────────────────────────────────────────────

export interface GoNoGoConfig {
  maxReconciliationMismatchRate: number;
  maxSupportCaseRate: number;
  minRecordingUptime: number;
  maxHoldbackReleaseDelayHours: number;
  minDisputeWithinSlaRate: number;
  updatedAt: string;
  updatedBy?: string;
}

export interface GoNoGoCriterion {
  key: string;
  label: string;
  passing: boolean;
  threshold: number;
  observed: number;
  unit: 'rate' | 'hours';
  note?: string;
}

export interface GoNoGoDashboard {
  generatedAt: string;
  config: GoNoGoConfig;
  criteria: GoNoGoCriterion[];
  greenlight: boolean;
}

// ── API surface ──────────────────────────────────────────────────────────────

export function getIqmaxHealth(): Promise<ProductHealthDashboard> {
  return apiGet('/api/v1/admin/dashboard/iqmax-health');
}

export function getEarningsDashboard(): Promise<EarningsDashboard> {
  return apiGet('/api/v1/admin/dashboard/earnings');
}

export function getOpsDashboard(): Promise<OpsDashboard> {
  return apiGet('/api/v1/admin/dashboard/ops');
}

export function getGoNoGo(): Promise<GoNoGoDashboard> {
  return apiGet('/api/v1/admin/dashboard/go-no-go');
}

export function updateGoNoGoConfig(
  patch: Partial<Omit<GoNoGoConfig, 'updatedAt' | 'updatedBy'>>,
): Promise<{ config: GoNoGoConfig }> {
  return apiPut('/api/v1/admin/dashboard/go-no-go/config', patch);
}

export function runParentProgressAudit(): Promise<{
  runId: string;
  status: 'ok' | 'mismatch' | 'error';
  mismatches: unknown[];
}> {
  return apiPost('/api/v1/admin/reconciliation/parent-progress/run', {});
}
